// Internationalized version of lifestyle categories
// This file provides i18n-enabled lifestyle categories that work with react-i18next

import { useTranslation } from 'react-i18next';

// Define a standard 5-gradient Likert scale for health-related questions
export const healthLikertScaleOptions = [
  { value: 1, labelKey: 'lifestyle.options.healthScale.1' },
  { value: 2, labelKey: 'lifestyle.options.healthScale.2' },
  { value: 3, labelKey: 'lifestyle.options.healthScale.3' },
  { value: 4, labelKey: 'lifestyle.options.healthScale.4' },
  { value: 5, labelKey: 'lifestyle.options.healthScale.5' },
];

// Define options for activity days (0-7 days scale)
export const activityDaysOptionsI18n = [
  { value: 'never', labelKey: 'lifestyle.options.activityDays.never' },
  { value: 'rarely', labelKey: 'lifestyle.options.activityDays.rarely' },
  { value: 'sometimes', labelKey: 'lifestyle.options.activityDays.sometimes' },
  { value: 'often', labelKey: 'lifestyle.options.activityDays.often' },
  { value: 'always', labelKey: 'lifestyle.options.activityDays.always' },
];

// Define options for specific questions with predefined answers
export const incidentalWalkingTimeOptionsI18n = [
  { value: '>60', labelKey: 'lifestyle.options.incidental_walking_time.>60' },
  { value: '45-59', labelKey: 'lifestyle.options.incidental_walking_time.45-59' },
  { value: '30-44', labelKey: 'lifestyle.options.incidental_walking_time.30-44' },
  { value: '15-29', labelKey: 'lifestyle.options.incidental_walking_time.15-29' },
  { value: '<15', labelKey: 'lifestyle.options.incidental_walking_time.<15' },
];

export const sedentaryHoursOptionsI18n = [
  { value: '<4', labelKey: 'lifestyle.options.sedentary_hours.<4' },
  { value: '4-5', labelKey: 'lifestyle.options.sedentary_hours.4-5' },
  { value: '6-7', labelKey: 'lifestyle.options.sedentary_hours.6-7' },
  { value: '8-9', labelKey: 'lifestyle.options.sedentary_hours.8-9' },
  { value: '>10', labelKey: 'lifestyle.options.sedentary_hours.>10' },
];

export const sittingBreakDaysOptionsI18n = [
  { value: 'always', labelKey: 'lifestyle.options.sitting_break_days.always' },
  { value: 'often', labelKey: 'lifestyle.options.sitting_break_days.often' },
  { value: 'sometimes', labelKey: 'lifestyle.options.sitting_break_days.sometimes' },
  { value: 'rarely', labelKey: 'lifestyle.options.sitting_break_days.rarely' },
  { value: 'never', labelKey: 'lifestyle.options.sitting_break_days.never' },
];

// Function to get lifestyle categories with i18n support
export const getLifestyleCategoriesI18n = (t) => [
  {
    key: 'nutrition',
    title: t('lifestyle.categories.nutrition'),
    parameters: [
      {
        key: 'protein_intake',
        name: 'Protein intake',
        questionText: t('lifestyle.questions.protein_intake'),
        type: 'Questionnaire',
        options: [
          { value: 'plant', labelKey: 'lifestyle.options.protein_intake.plant' },
          { value: 'fish', labelKey: 'lifestyle.options.protein_intake.fish' },
          { value: 'mix', labelKey: 'lifestyle.options.protein_intake.mix' },
          { value: 'red_meat', labelKey: 'lifestyle.options.protein_intake.red_meat' },
          { value: 'processed_meat', labelKey: 'lifestyle.options.protein_intake.processed_meat' }
        ],
        is_core_component: true,
        clinicalInterpretation: 'Type of protein consumed impacts overall health and chronic disease risk.'
      },
      {
        key: 'fruit_intake',
        name: 'Fruit intake',
        questionText: t('lifestyle.questions.fruit_intake'),
        type: 'Questionnaire',
        options: [
          { value: '4+', labelKey: 'lifestyle.options.fruit_intake.4+', label: '4 pieces or more' },
          { value: '3', labelKey: 'lifestyle.options.fruit_intake.3', label: '3 pieces' },
          { value: '2', labelKey: 'lifestyle.options.fruit_intake.2', label: '2 pieces' },
          { value: '1', labelKey: 'lifestyle.options.fruit_intake.1', label: '1 piece' },
          { value: '0', labelKey: 'lifestyle.options.fruit_intake.0', label: '0 pieces' }
        ],
        is_core_component: true,
        clinicalInterpretation: 'Fruits are rich in vitamins, minerals, and fiber. Aim for at least 2-3 servings daily.'
      },
      {
        key: 'vegetables_intake',
        name: 'Vegetables intake',
        questionText: t('lifestyle.questions.vegetables_intake'),
        type: 'Questionnaire',
        options: [
          { value: '4+', labelKey: 'lifestyle.options.vegetables_intake.4+' },
          { value: '3', labelKey: 'lifestyle.options.vegetables_intake.3' },
          { value: '2', labelKey: 'lifestyle.options.vegetables_intake.2' },
          { value: '1', labelKey: 'lifestyle.options.vegetables_intake.1' },
          { value: '0', labelKey: 'lifestyle.options.vegetables_intake.0' }
        ],
        is_core_component: true,
        clinicalInterpretation: 'Vegetables provide essential nutrients and antioxidants. Aim for at least 3-5 servings daily.'
      },
      {
        key: 'refined_carbs_intake',
        name: 'Refined carbs intake',
        questionText: t('lifestyle.questions.refined_carbs_intake'),
        type: 'Questionnaire',
        options: activityDaysOptionsI18n,
        is_core_component: true,
        clinicalInterpretation: 'Frequent consumption of refined carbohydrates can negatively impact blood sugar and weight.'
      },
      {
        key: 'fiber_intake',
        name: 'Fiber intake',
        questionText: t('lifestyle.questions.fiber_intake'),
        type: 'Questionnaire',
        options: activityDaysOptionsI18n,
        is_core_component: true,
        clinicalInterpretation: 'Dietary fiber is important for digestive health and blood sugar control. Legumes are a good source.'
      },
      {
        key: 'seeds_nuts_intake',
        name: 'Seeds/nuts intake',
        questionText: t('lifestyle.questions.seeds_nuts_intake'),
        type: 'Questionnaire',
        options: activityDaysOptionsI18n,
        is_core_component: true,
        clinicalInterpretation: 'Seeds and nuts provide healthy fats, fiber, and micronutrients.'
      },
      {
        key: 'sodium_intake',
        name: 'Sodium intake',
        questionText: t('lifestyle.questions.sodium_intake'),
        type: 'Questionnaire',
        options: activityDaysOptionsI18n,
        is_core_component: true,
        clinicalInterpretation: 'High sodium intake is linked to high blood pressure. Be mindful of added salt.'
      },
      {
        key: 'sugar_intake',
        name: 'Sugar intake',
        questionText: t('lifestyle.questions.sugar_intake'),
        type: 'Questionnaire',
        options: [
          { value: '0', labelKey: 'lifestyle.options.sugar_intake.0' },
          { value: '1', labelKey: 'lifestyle.options.sugar_intake.1' },
          { value: '2', labelKey: 'lifestyle.options.sugar_intake.2' },
          { value: '3', labelKey: 'lifestyle.options.sugar_intake.3' },
          { value: '4+', labelKey: 'lifestyle.options.sugar_intake.4+' }
        ],
        is_core_component: true,
        clinicalInterpretation: 'Excessive sugar intake contributes to various health problems. Limit sugary foods and drinks.'
      },
      {
        key: 'fried_food_intake',
        name: 'Fried food intake',
        questionText: t('lifestyle.questions.fried_food_intake'),
        type: 'Questionnaire',
        options: activityDaysOptionsI18n,
        is_core_component: true,
        clinicalInterpretation: 'Fried foods are often high in unhealthy fats and calories.'
      },
      {
        key: 'healthy_oil_use',
        name: 'Healthy oil use',
        questionText: t('lifestyle.questions.healthy_oil_use'),
        type: 'Questionnaire',
        options: [
          { value: 'always', labelKey: 'lifestyle.options.healthy_oil_use.always' },
          { value: 'often', labelKey: 'lifestyle.options.healthy_oil_use.often' },
          { value: 'sometimes', labelKey: 'lifestyle.options.healthy_oil_use.sometimes' },
          { value: 'rarely', labelKey: 'lifestyle.options.healthy_oil_use.rarely' },
          { value: 'never', labelKey: 'lifestyle.options.healthy_oil_use.never' }
        ],
        is_core_component: true,
        clinicalInterpretation: 'Choosing plant-based oils over animal fats can improve heart health.'
      },
      {
        key: 'ultraprocessed_intake',
        name: 'Ultra-processed food intake',
        questionText: t('lifestyle.questions.ultraprocessed_intake'),
        type: 'Questionnaire',
        options: activityDaysOptionsI18n,
        is_core_component: true,
        clinicalInterpretation: 'Ultra-processed foods are often low in nutrients and high in unhealthy additives.'
      },
      {
        key: 'water_intake',
        name: 'Water intake',
        questionText: t('lifestyle.questions.water_intake'),
        type: 'Questionnaire',
        options: [
          { value: '10+', labelKey: 'lifestyle.options.water_intake.10+' },
          { value: '7-9', labelKey: 'lifestyle.options.water_intake.7-9' },
          { value: '5-6', labelKey: 'lifestyle.options.water_intake.5-6' },
          { value: '3-4', labelKey: 'lifestyle.options.water_intake.3-4' },
          { value: '0-2', labelKey: 'lifestyle.options.water_intake.0-2' }
        ],
        is_core_component: true,
        clinicalInterpretation: 'Adequate hydration is crucial for overall health and bodily functions.'
      },
      {
        key: 'alcohol_intake',
        name: 'Alcohol intake',
        questionText: t('lifestyle.questions.alcohol_intake'),
        type: 'Questionnaire',
        options: [
          { value: '0', labelKey: 'lifestyle.options.alcohol_intake.0' },
          { value: '1-3', labelKey: 'lifestyle.options.alcohol_intake.1-3' },
          { value: '4-7', labelKey: 'lifestyle.options.alcohol_intake.4-7' },
          { value: '8-14', labelKey: 'lifestyle.options.alcohol_intake.8-14' },
          { value: '14+', labelKey: 'lifestyle.options.alcohol_intake.14+' }
        ],
        is_core_component: true,
        clinicalInterpretation: 'Excessive alcohol consumption has numerous health risks. Moderation is key.'
      },
      // Nutrition Status Header (for form display)
      {
        key: 'nutrition_status_header',
        name: t('lifestyle.questions.nutrition_status_header'),
        questionText: t('lifestyle.questions.nutrition_status_header'),
        type: 'Header',
        is_core_component: false,
        clinicalInterpretation: ''
      },
      // Nutrition Status items (BooleanCheckbox for multi-select)
      {
        key: 'weight_loss_concern',
        name: 'Weight loss concern',
        questionText: t('lifestyle.questions.unexplained_weight_loss'),
        type: 'BooleanCheckbox',
        is_core_component: true,
        clinicalInterpretation: 'Unexplained weight loss can be a sign of underlying health issues.'
      },
      {
        key: 'weight_gain_concern',
        name: 'Weight gain concern',
        questionText: t('lifestyle.questions.unexplained_weight_gain'),
        type: 'BooleanCheckbox',
        is_core_component: true,
        clinicalInterpretation: 'Unexplained weight gain can also indicate health concerns or lifestyle imbalances.'
      },
      {
        key: 'appetite_lower_normal',
        name: 'Appetite lower than normal',
        questionText: t('lifestyle.questions.decreased_appetite'),
        type: 'BooleanCheckbox',
        is_core_component: true,
        clinicalInterpretation: 'Significant changes in appetite should be evaluated.'
      },
      {
        key: 'appetite_higher_normal',
        name: 'Appetite higher than normal',
        questionText: t('lifestyle.questions.increased_appetite'),
        type: 'BooleanCheckbox',
        is_core_component: true,
        clinicalInterpretation: 'Significant changes in appetite should be evaluated.'
      },
      {
        key: 'skip_meals_hungry',
        name: 'Skip meals when not hungry',
        questionText: t('lifestyle.questions.skipping_meals_not_hungry'),
        type: 'BooleanCheckbox',
        is_core_component: true,
        clinicalInterpretation: 'Regularly skipping meals can affect energy levels and nutrient intake.'
      },
      {
        key: 'full_small_amount',
        name: 'Feel full after small amount',
        questionText: t('lifestyle.questions.early_satiety'),
        type: 'BooleanCheckbox',
        is_core_component: true,
        clinicalInterpretation: 'Feeling full quickly (early satiety) can sometimes indicate digestive issues.'
      },
      {
        key: 'difficulty_chewing_swallowing',
        name: 'Difficulty chewing/swallowing',
        questionText: t('lifestyle.questions.difficulty_chewing_swallowing'),
        type: 'BooleanCheckbox',
        is_core_component: true,
        clinicalInterpretation: 'Difficulties with chewing or swallowing can impact nutrition and may need medical attention.'
      },
      {
        key: 'problems_taste_smell',
        name: 'Problems with taste/smell',
        questionText: t('lifestyle.questions.problems_taste_smell'),
        type: 'BooleanCheckbox',
        is_core_component: true,
        clinicalInterpretation: 'Changes in taste or smell can affect appetite and enjoyment of food.'
      },
      {
        key: 'worry_food_access',
        name: 'Worry about food access',
        questionText: t('lifestyle.questions.worry_food_access'),
        type: 'BooleanCheckbox',
        is_core_component: true,
        clinicalInterpretation: 'Food insecurity can significantly impact health and well-being.'
      },
      // Eating Behaviors Header (for form display)
      {
        key: 'eating_behaviors_header',
        name: t('lifestyle.questions.eating_behaviors_header'),
        questionText: t('lifestyle.questions.eating_behaviors_header'),
        type: 'Header',
        is_core_component: false,
        clinicalInterpretation: ''
      },
      // Eating Behaviors items (BooleanCheckbox for multi-select)
      {
        key: 'bored_eating',
        name: 'Bored eating',
        questionText: t('lifestyle.questions.bored_eating'),
        type: 'BooleanCheckbox',
        is_core_component: true,
        clinicalInterpretation: 'Eating due to emotions rather than hunger can lead to overconsumption.'
      },
      {
        key: 'busy_eating',
        name: 'Busy eating',
        questionText: t('lifestyle.questions.busy_eating'),
        type: 'BooleanCheckbox',
        is_core_component: true,
        clinicalInterpretation: 'Skipping meals due to busyness can disrupt metabolism and energy.'
      },
      {
        key: 'late_eating',
        name: 'Late eating',
        questionText: t('lifestyle.questions.late_eating'),
        type: 'BooleanCheckbox',
        is_core_component: true,
        clinicalInterpretation: 'Eating large meals late at night can interfere with sleep and digestion.'
      },
      {
        key: 'binge_eating',
        name: 'Binge eating',
        questionText: t('lifestyle.questions.binge_eating'),
        type: 'BooleanCheckbox',
        is_core_component: true,
        clinicalInterpretation: 'Binge eating can be a sign of disordered eating patterns.'
      },
      {
        key: 'guilty_eating',
        name: 'Guilty eating',
        questionText: t('lifestyle.questions.guilt_eating'),
        type: 'BooleanCheckbox',
        is_core_component: true,
        clinicalInterpretation: 'Feelings of guilt or anxiety around food can indicate an unhealthy relationship with eating.'
      },
      {
        key: 'rushed_eating',
        name: 'Rushed eating',
        questionText: t('lifestyle.questions.fast_eating'),
        type: 'BooleanCheckbox',
        is_core_component: true,
        clinicalInterpretation: 'Eating too quickly can lead to overeating and poor digestion.'
      },
      {
        key: 'distracted_eating',
        name: 'Distracted eating',
        questionText: t('lifestyle.questions.distracted_eating'),
        type: 'BooleanCheckbox',
        is_core_component: true,
        clinicalInterpretation: 'Distracted eating can lead to overconsumption as the body may not register fullness cues.'
      },
      {
        key: 'fad_diets',
        name: 'Following fad diets',
        questionText: t('lifestyle.questions.strict_dieting'),
        type: 'BooleanCheckbox',
        is_core_component: true,
        clinicalInterpretation: 'Fad diets are often unsustainable and may lack essential nutrients.'
      }
    ]
  },
  {
    key: 'movement',
    title: t('lifestyle.categories.movement'),
    parameters: [
      {
        key: 'moderate_activity_days',
        name: 'Moderate activity days',
        questionText: t('lifestyle.questions.moderate_activity_days'),
        type: 'Questionnaire',
        options: activityDaysOptionsI18n,
        is_core_component: true,
        clinicalInterpretation: 'Regular moderate activity is key for cardiovascular health and overall well-being.'
      },
      {
        key: 'vigorous_activity_days',
        name: 'Vigorous activity days',
        questionText: t('lifestyle.questions.vigorous_activity_days'),
        type: 'Questionnaire',
        options: activityDaysOptionsI18n,
        is_core_component: true,
        clinicalInterpretation: 'Vigorous activity provides additional cardiovascular benefits and improves fitness.'
      },
      {
        key: 'strength_training_days',
        name: 'Strength training days',
        questionText: t('lifestyle.questions.strength_training_days'),
        type: 'Questionnaire',
        options: activityDaysOptionsI18n,
        is_core_component: true,
        clinicalInterpretation: 'Strength training is important for muscle mass, bone density, and metabolism.'
      },
      {
        key: 'flexibility_stretching_days',
        name: 'Flexibility/stretching days',
        questionText: t('lifestyle.questions.flexibility_stretching_days'),
        type: 'Questionnaire',
        options: activityDaysOptionsI18n,
        is_core_component: true,
        clinicalInterpretation: 'Flexibility exercises help maintain range of motion and prevent injuries.'
      },
      {
        key: 'balance_practice_days',
        name: 'Balance practice days',
        questionText: t('lifestyle.questions.balance_practice_days'),
        type: 'Questionnaire',
        options: activityDaysOptionsI18n,
        is_core_component: true,
        clinicalInterpretation: 'Balance exercises are important for stability and fall prevention, especially as we age.'
      },
      {
        key: 'incidental_walking_time',
        name: 'Incidental walking time',
        questionText: t('lifestyle.questions.incidental_walking_time'),
        type: 'Questionnaire',
        options: incidentalWalkingTimeOptionsI18n,
        is_core_component: true,
        clinicalInterpretation: 'Incidental activity throughout the day contributes to overall energy expenditure and health.'
      },
      {
        key: 'sedentary_hours',
        name: 'Sedentary hours',
        questionText: t('lifestyle.questions.sedentary_hours'),
        type: 'Questionnaire',
        options: sedentaryHoursOptionsI18n,
        is_core_component: true,
        clinicalInterpretation: 'Prolonged sitting is a risk factor for various chronic diseases. Aim to minimize sedentary time.'
      },
      {
        key: 'sitting_break_days',
        name: 'Sitting break days',
        questionText: t('lifestyle.questions.sitting_break_days'),
        type: 'Questionnaire',
        options: sittingBreakDaysOptionsI18n,
        is_core_component: true,
        clinicalInterpretation: 'Regularly breaking up sitting time with movement is beneficial for health.'
      },
      // Exercise Red Flags Header
      {
        key: 'red_flag_symptoms_header',
        name: t('lifestyle.questions.exercise_red_flags'),
        questionText: t('lifestyle.questions.exercise_red_flags'),
        type: 'Header',
        is_core_component: false,
        clinicalInterpretation: ''
      },
      // Individual Red Flag Symptoms (matching original structure)
      {
        key: 'red_flag_sob',
        name: 'Symptoms - Dyspnea',
        questionText: t('lifestyle.questions.shortness_of_breath'),
        type: 'BooleanCheckbox',
        is_core_component: true,
        clinicalInterpretation: 'Unusual shortness of breath during activity may indicate heart or lung issues.'
      },
      {
        key: 'red_flag_chest_pain',
        name: 'Symptoms - Chest discomfort',
        questionText: t('lifestyle.questions.chest_pain'),
        type: 'BooleanCheckbox',
        is_core_component: true,
        clinicalInterpretation: 'Chest discomfort during activity should always be evaluated by a doctor.'
      },
      {
        key: 'red_flag_palpitations',
        name: 'Symptoms - Palpitation',
        questionText: t('lifestyle.questions.heart_palpitations'),
        type: 'BooleanCheckbox',
        is_core_component: true,
        clinicalInterpretation: 'Palpitations during exercise can sometimes signal an underlying heart condition.'
      },
      {
        key: 'red_flag_dizziness',
        name: 'Symptoms - Dizziness',
        questionText: t('lifestyle.questions.dizziness_fainting'),
        type: 'BooleanCheckbox',
        is_core_component: true,
        clinicalInterpretation: 'Dizziness or fainting during activity requires medical attention.'
      },
      {
        key: 'red_flag_pain',
        name: 'Symptoms - Pain',
        questionText: t('lifestyle.questions.joint_muscle_pain'),
        type: 'BooleanCheckbox',
        is_core_component: true,
        clinicalInterpretation: 'Pain that forces you to stop activity should be assessed to prevent injury.'
      },
      {
        key: 'red_flag_imbalance',
        name: 'Symptoms - Imbalance',
        questionText: t('lifestyle.questions.balance_issues'),
        type: 'BooleanCheckbox',
        is_core_component: true,
        clinicalInterpretation: 'Loss of balance or falls during activity can indicate neurological or musculoskeletal issues.'
      }
    ]
  },
  {
    key: 'recovery',
    title: t('lifestyle.categories.recovery'),
    parameters: [
      {
        key: 'sleep_quality',
        name: 'Sleep quality',
        questionText: t('lifestyle.questions.sleep_quality'),
        type: 'Questionnaire',
        options: [
          { value: 1, labelKey: 'lifestyle.options.sleep_quality.1' },
          { value: 2, labelKey: 'lifestyle.options.sleep_quality.2' },
          { value: 3, labelKey: 'lifestyle.options.sleep_quality.3' },
          { value: 4, labelKey: 'lifestyle.options.sleep_quality.4' },
          { value: 5, labelKey: 'lifestyle.options.sleep_quality.5' }
        ],
        is_core_component: true,
        clinicalInterpretation: 'Waking up refreshed is a key indicator of good sleep quality.'
      },
      {
        key: 'sleep_duration',
        name: 'Sleep duration',
        questionText: t('lifestyle.questions.sleep_duration'),
        type: 'Questionnaire',
        options: [
          { value: 1, labelKey: 'lifestyle.options.sleep_duration.1' },
          { value: 2, labelKey: 'lifestyle.options.sleep_duration.2' },
          { value: 3, labelKey: 'lifestyle.options.sleep_duration.3' },
          { value: 4, labelKey: 'lifestyle.options.sleep_duration.4' },
          { value: 5, labelKey: 'lifestyle.options.sleep_duration.5' }
        ],
        is_core_component: true,
        clinicalInterpretation: 'Most adults need 7-9 hours of sleep per night for optimal health.'
      },
      {
        key: 'sleep_continuity',
        name: 'Sleep continuity',
        questionText: t('lifestyle.questions.sleep_continuity'),
        type: 'Questionnaire',
        options: [
          { value: 1, labelKey: 'lifestyle.options.sleep_continuity.1' },
          { value: 2, labelKey: 'lifestyle.options.sleep_continuity.2' },
          { value: 3, labelKey: 'lifestyle.options.sleep_continuity.3' },
          { value: 4, labelKey: 'lifestyle.options.sleep_continuity.4' },
          { value: 5, labelKey: 'lifestyle.options.sleep_continuity.5' }
        ],
        is_core_component: true,
        clinicalInterpretation: 'Frequent awakenings can disrupt sleep cycles and reduce sleep quality.'
      },
      {
        key: 'sleep_onset',
        name: 'Sleep onset',
        questionText: t('lifestyle.questions.sleep_onset'),
        type: 'Questionnaire',
        options: [
          { value: 1, labelKey: 'lifestyle.options.sleep_onset.1' },
          { value: 2, labelKey: 'lifestyle.options.sleep_onset.2' },
          { value: 3, labelKey: 'lifestyle.options.sleep_onset.3' },
          { value: 4, labelKey: 'lifestyle.options.sleep_onset.4' },
          { value: 5, labelKey: 'lifestyle.options.sleep_onset.5' }
        ],
        is_core_component: true,
        clinicalInterpretation: 'Regularly taking a long time to fall asleep can be a sign of insomnia.'
      },
      {
        key: 'bedtime_consistency',
        name: 'Bedtime Consistency',
        questionText: t('lifestyle.questions.bedtime_consistency'),
        type: 'Questionnaire',
        options: [
          { value: 1, labelKey: 'lifestyle.options.bedtime_consistency.1' },
          { value: 2, labelKey: 'lifestyle.options.bedtime_consistency.2' },
          { value: 3, labelKey: 'lifestyle.options.bedtime_consistency.3' },
          { value: 4, labelKey: 'lifestyle.options.bedtime_consistency.4' },
          { value: 5, labelKey: 'lifestyle.options.bedtime_consistency.5' }
        ],
        is_core_component: true,
        clinicalInterpretation: 'A consistent sleep schedule helps regulate your body\'s internal clock.'
      },
      {
        key: 'late_bedtime',
        name: 'Late bedtime',
        questionText: t('lifestyle.questions.late_bedtime'),
        type: 'Questionnaire',
        options: [
          { value: 1, labelKey: 'lifestyle.options.late_bedtime.1' },
          { value: 2, labelKey: 'lifestyle.options.late_bedtime.2' },
          { value: 3, labelKey: 'lifestyle.options.late_bedtime.3' },
          { value: 4, labelKey: 'lifestyle.options.late_bedtime.4' },
          { value: 5, labelKey: 'lifestyle.options.late_bedtime.5' }
        ],
        is_core_component: true,
        clinicalInterpretation: 'Consistently late bedtimes can disrupt circadian rhythms and sleep quality.'
      },
      {
        key: 'sleep_environment_factors',
        name: 'Sleep environment',
        questionText: t('lifestyle.questions.sleep_environment_factors'),
        type: 'BooleanCheckbox',
        options: [
          { key: 'uncomfortable_mattress', labelKey: 'lifestyle.questions.uncomfortable_mattress' },
          { key: 'room_temperature', labelKey: 'lifestyle.questions.room_temperature' },
          { key: 'bright_light', labelKey: 'lifestyle.questions.bright_light' },
          { key: 'loud_noises', labelKey: 'lifestyle.questions.loud_noises' },
          { key: 'electronic_screens', labelKey: 'lifestyle.questions.electronic_screens' },
          { key: 'caffeine_stimulants', labelKey: 'lifestyle.questions.caffeine_stimulants' }
        ],
        is_core_component: false,
        clinicalInterpretation: 'Various environmental factors can impact sleep quality.'
      },
      {
        key: 'napping_frequency',
        name: 'Napping frequency',
        questionText: t('lifestyle.questions.napping_frequency'),
        type: 'Questionnaire',
        options: [
          { value: 1, labelKey: 'lifestyle.options.sleep_continuity.1' },
          { value: 2, labelKey: 'lifestyle.options.sleep_continuity.2' },
          { value: 3, labelKey: 'lifestyle.options.sleep_continuity.3' },
          { value: 4, labelKey: 'lifestyle.options.sleep_continuity.4' },
          { value: 5, labelKey: 'lifestyle.options.sleep_continuity.5' }
        ],
        is_core_component: true,
        clinicalInterpretation: 'Frequent long naps can interfere with nighttime sleep quality.'
      },
      {
        key: 'daytime_sleepiness',
        name: 'Daytime sleepiness',
        questionText: t('lifestyle.questions.daytime_sleepiness'),
        type: 'Questionnaire',
        options: [
          { value: 1, labelKey: 'lifestyle.options.sleep_continuity.1' },
          { value: 2, labelKey: 'lifestyle.options.sleep_continuity.2' },
          { value: 3, labelKey: 'lifestyle.options.sleep_continuity.3' },
          { value: 4, labelKey: 'lifestyle.options.sleep_continuity.4' },
          { value: 5, labelKey: 'lifestyle.options.sleep_continuity.5' }
        ],
        is_core_component: true,
        clinicalInterpretation: 'Excessive daytime sleepiness can be a symptom of insufficient or poor-quality sleep.'
      },
      {
        key: 'sleep_disorders',
        name: 'Sleep disorders',
        questionText: t('lifestyle.questions.sleep_disorders'),
        type: 'BooleanCheckbox',
        options: [
          { key: 'waking_drenched_sweats', labelKey: 'lifestyle.questions.waking_drenched_sweats' },
          { key: 'teeth_grinding', labelKey: 'lifestyle.questions.teeth_grinding' },
          { key: 'nasal_congestion_snoring', labelKey: 'lifestyle.questions.nasal_congestion_snoring' },
          { key: 'restless_legs', labelKey: 'lifestyle.questions.restless_legs' },
          { key: 'racing_thoughts', labelKey: 'lifestyle.questions.racing_thoughts' },
          { key: 'upsetting_dreams', labelKey: 'lifestyle.questions.upsetting_dreams' }
        ],
        is_core_component: false,
        clinicalInterpretation: 'Various sleep-related issues can significantly affect health and recovery.'
      }
    ]
  },
  {
    key: 'emotion',
    title: t('lifestyle.categories.emotion'),
    parameters: [
      {
        key: 'little_interest',
        name: 'Little Interest',
        questionText: t('lifestyle.questions.little_interest'),
        type: 'Questionnaire',
        options: [
          { value: 0, labelKey: 'lifestyle.options.emotionScale.0' },
          { value: 1, labelKey: 'lifestyle.options.emotionScale.1' },
          { value: 2, labelKey: 'lifestyle.options.emotionScale.2' },
          { value: 3, labelKey: 'lifestyle.options.emotionScale.3' }
        ],
        is_core_component: true,
        clinicalInterpretation: 'Lack of interest or pleasure (anhedonia) is a key symptom of depression.'
      },
      {
        key: 'feeling_down',
        name: 'Feeling Down',
        questionText: t('lifestyle.questions.feeling_down'),
        type: 'Questionnaire',
        options: [
          { value: 0, labelKey: 'lifestyle.options.emotionScale.0' },
          { value: 1, labelKey: 'lifestyle.options.emotionScale.1' },
          { value: 2, labelKey: 'lifestyle.options.emotionScale.2' },
          { value: 3, labelKey: 'lifestyle.options.emotionScale.3' }
        ],
        is_core_component: true,
        clinicalInterpretation: 'Persistent feelings of sadness or hopelessness are common in depression.'
      },
      {
        key: 'feeling_nervous',
        name: 'Feeling Nervous',
        questionText: t('lifestyle.questions.feeling_nervous'),
        type: 'Questionnaire',
        options: [
          { value: 0, labelKey: 'lifestyle.options.emotionScale.0' },
          { value: 1, labelKey: 'lifestyle.options.emotionScale.1' },
          { value: 2, labelKey: 'lifestyle.options.emotionScale.2' },
          { value: 3, labelKey: 'lifestyle.options.emotionScale.3' }
        ],
        is_core_component: true,
        clinicalInterpretation: 'Frequent nervousness or anxiety can significantly impact daily life.'
      },
      {
        key: 'not_being_able',
        name: 'Not Being Able to Control Worrying',
        questionText: t('lifestyle.questions.not_being_able'),
        type: 'Questionnaire',
        options: [
          { value: 0, labelKey: 'lifestyle.options.emotionScale.0' },
          { value: 1, labelKey: 'lifestyle.options.emotionScale.1' },
          { value: 2, labelKey: 'lifestyle.options.emotionScale.2' },
          { value: 3, labelKey: 'lifestyle.options.emotionScale.3' }
        ],
        is_core_component: true,
        clinicalInterpretation: 'Excessive and uncontrollable worry is a hallmark of anxiety disorders.'
      },
      {
        key: 'difficulty_concentrating',
        name: 'Difficulty Concentrating',
        questionText: t('lifestyle.questions.difficulty_concentrating'),
        type: 'Questionnaire',
        options: [
          { value: 0, labelKey: 'lifestyle.options.emotionScale.0' },
          { value: 1, labelKey: 'lifestyle.options.emotionScale.1' },
          { value: 2, labelKey: 'lifestyle.options.emotionScale.2' },
          { value: 3, labelKey: 'lifestyle.options.emotionScale.3' }
        ],
        is_core_component: true,
        clinicalInterpretation: 'Concentration difficulties can be related to stress, anxiety, depression, or other conditions.'
      },
      {
        key: 'feeling_irritable',
        name: 'Feeling Irritable',
        questionText: t('lifestyle.questions.feeling_irritable'),
        type: 'Questionnaire',
        options: [
          { value: 0, labelKey: 'lifestyle.options.emotionScale.0' },
          { value: 1, labelKey: 'lifestyle.options.emotionScale.1' },
          { value: 2, labelKey: 'lifestyle.options.emotionScale.2' },
          { value: 3, labelKey: 'lifestyle.options.emotionScale.3' }
        ],
        is_core_component: true,
        clinicalInterpretation: 'Increased irritability can be a symptom of stress, mood disorders, or other issues.'
      },
      {
        key: 'felt_unable_control',
        name: 'Felt Unable to Control',
        questionText: t('lifestyle.questions.unable_to_control'),
        type: 'Questionnaire',
        options: [
          { value: 0, labelKey: 'lifestyle.options.stressControlScale.0' },
          { value: 1, labelKey: 'lifestyle.options.stressControlScale.1' },
          { value: 2, labelKey: 'lifestyle.options.stressControlScale.2' },
          { value: 3, labelKey: 'lifestyle.options.stressControlScale.3' },
          { value: 4, labelKey: 'lifestyle.options.stressControlScale.4' }
        ],
        is_core_component: true,
        clinicalInterpretation: 'Feeling unable to control important life events is associated with stress and mental health issues.'
      },
      {
        key: 'felt_confident_problems',
        name: 'Felt Confident About Problems',
        questionText: t('lifestyle.questions.confident_handling_problems'),
        type: 'Questionnaire',
        options: [
          { value: 0, labelKey: 'lifestyle.options.stressControlScale.0' },
          { value: 1, labelKey: 'lifestyle.options.stressControlScale.1' },
          { value: 2, labelKey: 'lifestyle.options.stressControlScale.2' },
          { value: 3, labelKey: 'lifestyle.options.stressControlScale.3' },
          { value: 4, labelKey: 'lifestyle.options.stressControlScale.4' }
        ],
        is_core_component: true,
        clinicalInterpretation: 'Confidence in handling personal problems is a measure of self-efficacy and resilience.'
      },
      {
        key: 'felt_things_going_way',
        name: 'Felt Things Going Your Way',
        questionText: t('lifestyle.questions.things_going_your_way'),
        type: 'Questionnaire',
        options: [
          { value: 0, labelKey: 'lifestyle.options.stressControlScale.0' },
          { value: 1, labelKey: 'lifestyle.options.stressControlScale.1' },
          { value: 2, labelKey: 'lifestyle.options.stressControlScale.2' },
          { value: 3, labelKey: 'lifestyle.options.stressControlScale.3' },
          { value: 4, labelKey: 'lifestyle.options.stressControlScale.4' }
        ],
        is_core_component: true,
        clinicalInterpretation: 'Feeling that things are going well reflects positive coping and life satisfaction.'
      },
      {
        key: 'felt_difficulties_piling_up',
        name: 'Felt Difficulties Piling Up',
        questionText: t('lifestyle.questions.difficulties_piling_up'),
        type: 'Questionnaire',
        options: [
          { value: 0, labelKey: 'lifestyle.options.stressControlScale.0' },
          { value: 1, labelKey: 'lifestyle.options.stressControlScale.1' },
          { value: 2, labelKey: 'lifestyle.options.stressControlScale.2' },
          { value: 3, labelKey: 'lifestyle.options.stressControlScale.3' },
          { value: 4, labelKey: 'lifestyle.options.stressControlScale.4' }
        ],
        is_core_component: true,
        clinicalInterpretation: 'Feeling overwhelmed by difficulties is a key indicator of stress and potential mental health concerns.'
      },
      {
        key: 'lack_companionship',
        name: 'Lack Companionship',
        questionText: t('lifestyle.questions.lack_companionship'),
        type: 'Questionnaire',
        options: [
          { value: 0, labelKey: 'lifestyle.options.lonelinessScale.0' },
          { value: 1, labelKey: 'lifestyle.options.lonelinessScale.1' },
          { value: 2, labelKey: 'lifestyle.options.lonelinessScale.2' }
        ],
        is_core_component: true,
        clinicalInterpretation: 'Lack of companionship is a key component of loneliness, which impacts mental and physical health.'
      },
      {
        key: 'feel_left_out',
        name: 'Feel Left Out',
        questionText: t('lifestyle.questions.feel_left_out'),
        type: 'Questionnaire',
        options: [
          { value: 0, labelKey: 'lifestyle.options.lonelinessScale.0' },
          { value: 1, labelKey: 'lifestyle.options.lonelinessScale.1' },
          { value: 2, labelKey: 'lifestyle.options.lonelinessScale.2' }
        ],
        is_core_component: true,
        clinicalInterpretation: 'Feeling left out reflects social isolation and can contribute to depression and anxiety.'
      },
      {
        key: 'feel_isolated',
        name: 'Feel Isolated',
        questionText: t('lifestyle.questions.feel_isolated'),
        type: 'Questionnaire',
        options: [
          { value: 0, labelKey: 'lifestyle.options.lonelinessScale.0' },
          { value: 1, labelKey: 'lifestyle.options.lonelinessScale.1' },
          { value: 2, labelKey: 'lifestyle.options.lonelinessScale.2' }
        ],
        is_core_component: true,
        clinicalInterpretation: 'Social isolation is strongly linked to poor mental and physical health outcomes.'
      },
      // Burnout/Emotional Issues Header
      {
        key: 'emotional_burnout_header',
        name: t('lifestyle.questions.emotional_burnout_header'),
        questionText: t('lifestyle.questions.emotional_burnout_header'),
        type: 'Header',
        is_core_component: false,
        clinicalInterpretation: ''
      },
      // Individual Burnout Symptoms (matching original structure)
      {
        key: 'burnout_emotionally_exhausted',
        name: 'Burnout - Emotionally exhausted',
        questionText: t('lifestyle.questions.emotional_exhaustion'),
        type: 'BooleanCheckbox',
        is_core_component: true,
        clinicalInterpretation: 'Emotional exhaustion is a core symptom of burnout.'
      },
      {
        key: 'burnout_cynical',
        name: 'Burnout - Cynical',
        questionText: t('lifestyle.questions.cynicism_detachment'),
        type: 'BooleanCheckbox',
        is_core_component: true,
        clinicalInterpretation: 'Cynicism or detachment can be a sign of burnout.'
      },
      {
        key: 'burnout_cant_disconnect',
        name: "Burnout - Can't disconnect",
        questionText: t('lifestyle.questions.unable_to_disconnect'),
        type: 'BooleanCheckbox',
        is_core_component: true,
        clinicalInterpretation: 'Inability to disconnect from work is a common feature of burnout.'
      },
      {
        key: 'burnout_no_breaks',
        name: 'Burnout - No breaks',
        questionText: t('lifestyle.questions.no_real_breaks'),
        type: 'BooleanCheckbox',
        is_core_component: true,
        clinicalInterpretation: 'Lack of adequate breaks and rest contributes to burnout.'
      },
      {
        key: 'burnout_long_hours',
        name: 'Burnout - Long hours',
        questionText: t('lifestyle.questions.working_long_hours'),
        type: 'BooleanCheckbox',
        is_core_component: true,
        clinicalInterpretation: 'Consistently working long hours increases the risk of burnout.'
      },
      {
        key: 'burnout_personal_time',
        name: 'Burnout - Personal time',
        questionText: t('lifestyle.questions.no_personal_time'),
        type: 'BooleanCheckbox',
        is_core_component: true,
        clinicalInterpretation: 'Insufficient time for personal life is a factor in burnout.'
      },
      {
        key: 'burnout_family_time',
        name: 'Burnout - Family time',
        questionText: t('lifestyle.questions.no_family_time'),
        type: 'BooleanCheckbox',
        is_core_component: true,
        clinicalInterpretation: 'Work encroaching on family/social time can lead to burnout.'
      },
      {
        key: 'burnout_purpose',
        name: 'Burnout - Purpose (meaning)',
        questionText: t('lifestyle.questions.work_meaninglessness'),
        type: 'BooleanCheckbox',
        is_core_component: true,
        clinicalInterpretation: 'Loss of meaning or purpose in work is a significant aspect of burnout.'
      }
    ]
  },
  {
    key: 'environment',
    title: t('lifestyle.categories.environment'),
    parameters: [
      {
        key: 'smoking_quantity_daily',
        name: 'Smoking Quantity (Daily)',
        questionText: t('lifestyle.questions.smoking_quantity_daily'),
        type: 'Questionnaire',
        options: [
          { value: 'none', labelKey: 'lifestyle.options.smoking_quantity_daily.none' },
          { value: '1-5', labelKey: 'lifestyle.options.smoking_quantity_daily.1-5' },
          { value: '6-10', labelKey: 'lifestyle.options.smoking_quantity_daily.6-10' },
          { value: '11-20', labelKey: 'lifestyle.options.smoking_quantity_daily.11-20' },
          { value: 'gt20', labelKey: 'lifestyle.options.smoking_quantity_daily.gt20' }
        ],
        is_core_component: true,
        clinicalInterpretation: 'Daily smoking quantity is a major risk factor for numerous health conditions.'
      },
      {
        key: 'smoking_duration_years',
        name: 'Smoking Duration (Years)',
        questionText: t('lifestyle.questions.smoking_duration_years'),
        type: 'Questionnaire',
        options: [
          { value: 'never', labelKey: 'lifestyle.options.smoking_duration_years.never' },
          { value: 'lt1', labelKey: 'lifestyle.options.smoking_duration_years.lt1' },
          { value: '1-5', labelKey: 'lifestyle.options.smoking_duration_years.1-5' },
          { value: '6-10', labelKey: 'lifestyle.options.smoking_duration_years.6-10' },
          { value: 'gt10', labelKey: 'lifestyle.options.smoking_duration_years.gt10' }
        ],
        is_core_component: true,
        clinicalInterpretation: 'Duration of smoking significantly impacts cumulative health risks.'
      },
      {
        key: 'outdoor_daylight_minutes',
        name: 'Outdoor Daylight Exposure (Daily)',
        questionText: t('lifestyle.questions.outdoor_daylight_minutes'),
        type: 'Questionnaire',
        options: [
          { value: '0', labelKey: 'lifestyle.options.outdoor_daylight_minutes.0' },
          { value: '1-15', labelKey: 'lifestyle.options.outdoor_daylight_minutes.1-15' },
          { value: '16-30', labelKey: 'lifestyle.options.outdoor_daylight_minutes.16-30' },
          { value: '31-60', labelKey: 'lifestyle.options.outdoor_daylight_minutes.31-60' },
          { value: 'gt60', labelKey: 'lifestyle.options.outdoor_daylight_minutes.gt60' }
        ],
        is_core_component: true,
        clinicalInterpretation: 'Exposure to natural daylight is important for circadian rhythm regulation and Vitamin D synthesis.'
      },
      {
        key: 'nature_time_days_weekly',
        name: 'Time in Nature (Weekly)',
        questionText: t('lifestyle.questions.nature_time_days_weekly'),
        type: 'Questionnaire',
        options: [
          { value: '0', labelKey: 'lifestyle.options.nature_time_days_weekly.0', label: '0 days' },
          { value: '1-2', labelKey: 'lifestyle.options.nature_time_days_weekly.1-2', label: '1–2 days' },
          { value: '3-4', labelKey: 'lifestyle.options.nature_time_days_weekly.3-4', label: '3–4 days' },
          { value: '5-6', labelKey: 'lifestyle.options.nature_time_days_weekly.5-6', label: '5–6 days' },
          { value: '7', labelKey: 'lifestyle.options.nature_time_days_weekly.7', label: '7 days' }
        ],
        is_core_component: true,
        clinicalInterpretation: 'Spending time in natural environments has been linked to improved mental and physical well-being.'
      },
      {
        key: 'morning_outdoor_light_days_weekly',
        name: 'Morning Outdoor Light (Weekly)',
        questionText: t('lifestyle.questions.morning_outdoor_light_days_weekly'),
        type: 'Questionnaire',
        options: [
          { value: 'never', labelKey: 'lifestyle.options.morning_outdoor_light_days_weekly.never' },
          { value: 'rarely', labelKey: 'lifestyle.options.morning_outdoor_light_days_weekly.rarely' },
          { value: 'sometimes', labelKey: 'lifestyle.options.morning_outdoor_light_days_weekly.sometimes' },
          { value: 'often', labelKey: 'lifestyle.options.morning_outdoor_light_days_weekly.often' },
          { value: 'always', labelKey: 'lifestyle.options.morning_outdoor_light_days_weekly.always' }
        ],
        is_core_component: true,
        clinicalInterpretation: 'Morning light exposure helps anchor the circadian rhythm, which can improve sleep and mood.'
      }
    ]
  }
];

// Hook to get lifestyle categories with current language
export const useLifestyleCategoriesI18n = () => {
  const { t } = useTranslation();
  return getLifestyleCategoriesI18n(t);
};

// Helper function to get translated option label
export const getTranslatedOptionLabel = (option, t) => {
  if (option.labelKey) {
    return t(option.labelKey);
  }
  if (option.label) {
    return option.label;
  }
  // For checkbox options that use the question key directly
  if (option.key) {
    return t(`lifestyle.questions.${option.key}`, { defaultValue: option.key });
  }
  return option.value;
};

// Helper function to get parameters for a specific lifestyle category with i18n
export const getParametersForLifestyleCategoryI18n = (categoryKey, t) => {
  const categories = getLifestyleCategoriesI18n(t);
  const category = categories.find(cat => cat.key === categoryKey);
  return category ? category.parameters : [];
};

// Helper function to get all lifestyle parameters flattened with i18n
export const getAllLifestyleParametersI18n = (t) => {
  const categories = getLifestyleCategoriesI18n(t);
  return categories.reduce((acc, category) => {
    return acc.concat(category.parameters);
  }, []);
};
